import { CONFIG } from '../data/config';

/** The in-game clock. `day` only advances on sleep, not on passing midnight —
 *  staying up late keeps the same day until you sleep (Stardew-style). */
export interface GameClock {
  /** 1-based day counter (increments on sleep). */
  day: number;
  hour: number; // 0–23
  minute: number; // 0–59
}

export const newClock = (): GameClock => ({ day: 1, hour: CONFIG.dayStartHour, minute: 0 });

/** Advance the wall clock by whole game-minutes. Wraps past midnight but does
 *  NOT change `day`. Pure. */
export function addGameMinutes(clock: GameClock, minutes: number): GameClock {
  const total = clock.hour * 60 + clock.minute + Math.max(0, Math.floor(minutes));
  const wrapped = ((total % 1440) + 1440) % 1440;
  return { day: clock.day, hour: Math.floor(wrapped / 60), minute: wrapped % 60 };
}

/** True in the forced-collapse window [forceSleepHour, dayStartHour). */
export function isPastForceSleep(clock: GameClock): boolean {
  return clock.hour >= CONFIG.forceSleepHour && clock.hour < CONFIG.dayStartHour;
}

/** Minutes since the day began at dayStartHour (handles after-midnight). */
export function minutesSinceDayStart(clock: GameClock): number {
  const start = CONFIG.dayStartHour * 60;
  const now = clock.hour * 60 + clock.minute;
  return ((now - start) % 1440 + 1440) % 1440;
}

/** Real elapsed milliseconds → game-minutes at the configured rate. */
export function realMsToGameMinutes(
  ms: number,
  realSecondsPerGameMinute: number = CONFIG.realSecondsPerGameMinute,
): number {
  return ms / 1000 / realSecondsPerGameMinute;
}

/** The clock you wake to after sleeping: next day at dayStartHour. */
export function wakeClock(clock: GameClock): GameClock {
  return { day: clock.day + 1, hour: CONFIG.dayStartHour, minute: 0 };
}

/** 12-hour display string, e.g. "6:05 AM". */
export function formatClock(clock: GameClock): string {
  const h12 = clock.hour % 12 === 0 ? 12 : clock.hour % 12;
  const ampm = clock.hour < 12 ? 'AM' : 'PM';
  const mm = clock.minute.toString().padStart(2, '0');
  return `${h12}:${mm} ${ampm}`;
}
